import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { intakeAPI } from '../../lib/api';
import { 
  ClipboardText, 
  Check,
  CaretRight,
  CaretLeft,
  Warning,
  Heart,
  Thermometer,
  Wind,
  Heartbeat
} from '@phosphor-icons/react';

const symptomOptions = [
  'Fever', 'Cough', 'Headache', 'Fatigue', 'Nausea', 'Dizziness',
  'Chest Pain', 'Shortness of Breath', 'Abdominal Pain', 'Back Pain',
  'Joint Pain', 'Sore Throat', 'Runny Nose', 'Muscle Aches', 'Chills'
];

export default function PatientIntake() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    chief_complaint: '',
    symptoms: [],
    symptom_duration: '',
    pain_level: 0,
    medications: [],
    allergies: [],
    medical_history: [],
    patient_vital_signs: {
      blood_pressure_systolic: '',
      blood_pressure_diastolic: '',
      temperature: '',
      respiratory_rate: '',
      pulse: '',
      height: '',
      weight: ''
    }
  });
  const [medicationInput, setMedicationInput] = useState('');
  const [allergyInput, setAllergyInput] = useState('');
  const [historyInput, setHistoryInput] = useState('');

  const toggleSymptom = (symptom) => {
    setFormData(prev => ({
      ...prev,
      symptoms: prev.symptoms.includes(symptom)
        ? prev.symptoms.filter(s => s !== symptom)
        : [...prev.symptoms, symptom]
    }));
  };

  const addItem = (field, value, setInput) => {
    if (value.trim()) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()]
      }));
      setInput('');
    }
  };

  const removeItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Format vital signs for submission
      const vitals = formData.patient_vital_signs;
      const hasVitals = vitals.blood_pressure_systolic || vitals.temperature || vitals.respiratory_rate || vitals.pulse || vitals.height || vitals.weight;
      
      const submitData = {
        ...formData,
        patient_vital_signs: hasVitals ? {
          blood_pressure: vitals.blood_pressure_systolic && vitals.blood_pressure_diastolic 
            ? `${vitals.blood_pressure_systolic}/${vitals.blood_pressure_diastolic}` 
            : null,
          temperature: vitals.temperature ? parseFloat(vitals.temperature) : null,
          respiratory_rate: vitals.respiratory_rate ? parseInt(vitals.respiratory_rate) : null,
          heart_rate: vitals.pulse ? parseInt(vitals.pulse) : null,
          height: vitals.height ? parseFloat(vitals.height) : null,
          weight: vitals.weight ? parseFloat(vitals.weight) : null
        } : null
      };
      
      await intakeAPI.create(submitData);
      navigate('/patient');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit intake form');
    } finally {
      setLoading(false);
    }
  };

  const updateVitalSign = (field, value) => {
    setFormData(prev => ({
      ...prev,
      patient_vital_signs: {
        ...prev.patient_vital_signs,
        [field]: value
      }
    }));
  };

  const canProceed = () => {
    switch (step) {
      case 1: return formData.chief_complaint.trim() !== '';
      case 2: return formData.symptoms.length > 0 && formData.symptom_duration !== '';
      case 3: return true; // Medical history is optional
      case 4: return true; // Vital signs are optional
      default: return true;
    }
  };

  return (
    <div className="p-8 max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
            <ClipboardText size={20} className="text-teal-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
              Health Intake Form
            </h1>
            <p className="text-slate-500 text-sm">Step {step} of 4</p>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="flex gap-2 mt-4">
          {[1, 2, 3, 4].map((s) => (
            <div
              key={s}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                s <= step ? 'bg-teal-500' : 'bg-slate-200'
              }`}
            />
          ))}
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
          <Warning size={20} />
          <span>{error}</span>
        </div>
      )}

      <div className="card-clinical p-8">
        {/* Step 1: Chief Complaint */}
        {step === 1 && (
          <div className="form-slide-in" data-testid="intake-step-1">
            <h2 className="text-lg font-semibold text-slate-900 mb-6">What brings you in today?</h2>
            
            <div className="mb-6">
              <label className="label-clinical">Describe your main concern</label>
              <textarea
                value={formData.chief_complaint}
                onChange={(e) => setFormData({ ...formData, chief_complaint: e.target.value })}
                className="input-clinical min-h-[120px]"
                placeholder="e.g., I've been experiencing headaches for the past week..."
                data-testid="chief-complaint-input"
              />
            </div>
          </div>
        )}

        {/* Step 2: Symptoms */}
        {step === 2 && (
          <div className="form-slide-in" data-testid="intake-step-2">
            <h2 className="text-lg font-semibold text-slate-900 mb-6">Tell us about your symptoms</h2>
            
            <div className="mb-6">
              <label className="label-clinical">Select all symptoms that apply</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {symptomOptions.map((symptom) => (
                  <button
                    key={symptom}
                    type="button"
                    onClick={() => toggleSymptom(symptom)}
                    data-testid={`symptom-${symptom.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      formData.symptoms.includes(symptom)
                        ? 'bg-teal-500 text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    {formData.symptoms.includes(symptom) && <Check size={14} className="inline mr-1" />}
                    {symptom}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <label className="label-clinical">How long have you had these symptoms?</label>
              <select
                value={formData.symptom_duration}
                onChange={(e) => setFormData({ ...formData, symptom_duration: e.target.value })}
                className="input-clinical"
                data-testid="symptom-duration"
              >
                <option value="">Select duration</option>
                <option value="Less than 24 hours">Less than 24 hours</option>
                <option value="1-3 days">1-3 days</option>
                <option value="4-7 days">4-7 days</option>
                <option value="1-2 weeks">1-2 weeks</option>
                <option value="More than 2 weeks">More than 2 weeks</option>
              </select>
            </div>

            <div className="mb-6">
              <label className="label-clinical">Pain Level (0-10)</label>
              <input
                type="range"
                min="0"
                max="10"
                value={formData.pain_level}
                onChange={(e) => setFormData({ ...formData, pain_level: parseInt(e.target.value) })}
                className="w-full"
                data-testid="pain-level"
              />
              <div className="flex justify-between text-xs text-slate-500 mt-1">
                <span>No Pain (0)</span>
                <span className="font-semibold text-teal-600">{formData.pain_level}</span>
                <span>Severe (10)</span>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Medical History */}
        {step === 3 && (
          <div className="form-slide-in" data-testid="intake-step-3">
            <h2 className="text-lg font-semibold text-slate-900 mb-6">Medical History</h2>
            
            <div className="mb-6">
              <label className="label-clinical">Current Medications</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={medicationInput}
                  onChange={(e) => setMedicationInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem('medications', medicationInput, setMedicationInput))}
                  className="input-clinical flex-1"
                  placeholder="Add medication and press Enter"
                  data-testid="medication-input"
                />
                <button
                  type="button"
                  onClick={() => addItem('medications', medicationInput, setMedicationInput)}
                  className="btn-outline"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.medications.map((med, idx) => (
                  <span key={`med-${med}-${idx}`} className="px-3 py-1 bg-slate-100 rounded-full text-sm flex items-center gap-2">
                    {med}
                    <button onClick={() => removeItem('medications', idx)} className="text-slate-400 hover:text-red-500">&times;</button>
                  </span>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <label className="label-clinical">Allergies</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={allergyInput}
                  onChange={(e) => setAllergyInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem('allergies', allergyInput, setAllergyInput))}
                  className="input-clinical flex-1"
                  placeholder="Add allergy and press Enter"
                  data-testid="allergy-input"
                />
                <button
                  type="button"
                  onClick={() => addItem('allergies', allergyInput, setAllergyInput)}
                  className="btn-outline"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.allergies.map((allergy, idx) => (
                  <span key={`allergy-${allergy}-${idx}`} className="px-3 py-1 bg-red-50 text-red-700 rounded-full text-sm flex items-center gap-2">
                    {allergy}
                    <button onClick={() => removeItem('allergies', idx)} className="hover:text-red-900">&times;</button>
                  </span>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <label className="label-clinical">Medical History</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={historyInput}
                  onChange={(e) => setHistoryInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem('medical_history', historyInput, setHistoryInput))}
                  className="input-clinical flex-1"
                  placeholder="Add condition and press Enter"
                  data-testid="history-input"
                />
                <button
                  type="button"
                  onClick={() => addItem('medical_history', historyInput, setHistoryInput)}
                  className="btn-outline"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.medical_history.map((hist, idx) => (
                  <span key={`hist-${hist}-${idx}`} className="px-3 py-1 bg-slate-100 rounded-full text-sm flex items-center gap-2">
                    {hist}
                    <button onClick={() => removeItem('medical_history', idx)} className="text-slate-400 hover:text-red-500">&times;</button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Vital Signs (Patient Self-Entry) */}
        {step === 4 && (
          <div className="form-slide-in" data-testid="intake-step-4">
            <h2 className="text-lg font-semibold text-slate-900 mb-2">Your Vital Signs (Optional)</h2>
            <p className="text-slate-500 text-sm mb-6">
              If you have home monitoring devices, enter your recent readings below. This helps our team prepare for your visit.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Blood Pressure */}
              <div className="p-4 bg-red-50 rounded-xl border border-red-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <Heart size={20} className="text-red-500" weight="fill" />
                  </div>
                  <div>
                    <label className="font-medium text-slate-900">Blood Pressure</label>
                    <p className="text-xs text-slate-500">e.g., 120/80 mmHg</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={formData.patient_vital_signs.blood_pressure_systolic}
                    onChange={(e) => updateVitalSign('blood_pressure_systolic', e.target.value)}
                    placeholder="120"
                    className="input-clinical w-20 text-center"
                    min="60"
                    max="250"
                    data-testid="bp-systolic"
                  />
                  <span className="text-slate-500 font-medium">/</span>
                  <input
                    type="number"
                    value={formData.patient_vital_signs.blood_pressure_diastolic}
                    onChange={(e) => updateVitalSign('blood_pressure_diastolic', e.target.value)}
                    placeholder="80"
                    className="input-clinical w-20 text-center"
                    min="40"
                    max="150"
                    data-testid="bp-diastolic"
                  />
                  <span className="text-sm text-slate-500">mmHg</span>
                </div>
              </div>

              {/* Temperature */}
              <div className="p-4 bg-orange-50 rounded-xl border border-orange-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Thermometer size={20} className="text-orange-500" weight="fill" />
                  </div>
                  <div>
                    <label className="font-medium text-slate-900">Temperature</label>
                    <p className="text-xs text-slate-500">Normal: 97.8-99.1°F</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    step="0.1"
                    value={formData.patient_vital_signs.temperature}
                    onChange={(e) => updateVitalSign('temperature', e.target.value)}
                    placeholder="98.6"
                    className="input-clinical w-24 text-center"
                    min="95"
                    max="107"
                    data-testid="temperature"
                  />
                  <span className="text-sm text-slate-500">°F</span>
                </div>
              </div>

              {/* Respiratory Rate */}
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Wind size={20} className="text-blue-500" weight="fill" />
                  </div>
                  <div>
                    <label className="font-medium text-slate-900">Respiratory Rate</label>
                    <p className="text-xs text-slate-500">Breaths per minute (normal: 12-20)</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={formData.patient_vital_signs.respiratory_rate}
                    onChange={(e) => updateVitalSign('respiratory_rate', e.target.value)}
                    placeholder="16"
                    className="input-clinical w-20 text-center"
                    min="8"
                    max="40"
                    data-testid="respiratory-rate"
                  />
                  <span className="text-sm text-slate-500">breaths/min</span>
                </div>
              </div>

              {/* Pulse */}
              <div className="p-4 bg-purple-50 rounded-xl border border-purple-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Heartbeat size={20} className="text-purple-500" weight="fill" />
                  </div>
                  <div>
                    <label className="font-medium text-slate-900">Pulse / Heart Rate</label>
                    <p className="text-xs text-slate-500">Beats per minute (normal: 60-100)</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={formData.patient_vital_signs.pulse}
                    onChange={(e) => updateVitalSign('pulse', e.target.value)}
                    placeholder="72"
                    className="input-clinical w-20 text-center"
                    min="40"
                    max="200"
                    data-testid="pulse"
                  />
                  <span className="text-sm text-slate-500">bpm</span>
                </div>
              </div>

              {/* Height */}
              <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-indigo-500">
                      <path d="M12 2v20M2 12h4M18 12h4M12 6h2M12 18h2" />
                    </svg>
                  </div>
                  <div>
                    <label className="font-medium text-slate-900">Height</label>
                    <p className="text-xs text-slate-500">Total inches (e.g., 5'8" = 68 inches)</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    step="0.5"
                    value={formData.patient_vital_signs.height}
                    onChange={(e) => updateVitalSign('height', e.target.value)}
                    placeholder="68"
                    className="input-clinical w-20 text-center"
                    min="36"
                    max="96"
                    data-testid="height"
                  />
                  <span className="text-sm text-slate-500">inches</span>
                </div>
              </div>

              {/* Weight */}
              <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-emerald-500">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 6v6l4 2" />
                    </svg>
                  </div>
                  <div>
                    <label className="font-medium text-slate-900">Weight</label>
                    <p className="text-xs text-slate-500">In pounds (lbs)</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    step="0.5"
                    value={formData.patient_vital_signs.weight}
                    onChange={(e) => updateVitalSign('weight', e.target.value)}
                    placeholder="150"
                    className="input-clinical w-24 text-center"
                    min="50"
                    max="600"
                    data-testid="weight"
                  />
                  <span className="text-sm text-slate-500">lbs</span>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-teal-50 rounded-xl border border-teal-100">
              <p className="text-sm text-teal-800">
                <strong>Tip:</strong> Don't worry if you don&apos;t have these readings. Our CMA will measure your vital signs when you arrive for your appointment.
              </p>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-8 pt-6 border-t border-slate-200">
          <button
            type="button"
            onClick={() => setStep(s => s - 1)}
            disabled={step === 1}
            className={`flex items-center gap-2 font-medium ${
              step === 1 ? 'text-slate-300' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <CaretLeft size={18} /> Back
          </button>
          
          {step < 4 ? (
            <button
              type="button"
              onClick={() => setStep(s => s + 1)}
              disabled={!canProceed()}
              className="btn-teal flex items-center gap-2"
              data-testid="intake-next"
            >
              Continue <CaretRight size={18} weight="bold" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="btn-teal flex items-center gap-2"
              data-testid="intake-submit"
            >
              {loading ? <div className="spinner" /> : <Check size={18} weight="bold" />}
              Submit Intake
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
